import { CloseOutlined } from "@mui/icons-material";
import {
    Alert,
    Button,
    CircularProgress,
    Grid,
    IconButton,
    TextField,
    Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { POST } from "../../../Utilities/BaseService";
import { MultiPermissionAuthorize } from "../../../Utilities/MultiPermissionAuthorize";
import UpdatedSearchableInput from "../../../Utilities/UpdatedSearchableInput";

const initialClearAll = {
    district: false,
    village: false,
    powerstation: false,
    hamlet: false,
};

const initialDefaultRegions = {
    district: "",
    village: "",
    hamlet: "",
};

const UpdateReadingDate = () => {
    const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM

    const [intermediateFilter, setIntermediateFilter] = useState<any>({});
    const [clearAll, setClearAll] = useState(initialClearAll);
    const [defaultRegions, setDefaultRegions] = useState(initialDefaultRegions);
    const [billingMonth, setBillingMonth] = useState("");
    const [issueDate, setIssueDate] = useState("");
    const [readingDate, setReadingDate] = useState("");
    const [dueDate, setDueDate] = useState("");
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState<any>({
        severity: "",
        message: "",
        show: false,
    });

    // Handle clearAll logic
    useEffect(() => {
        if (intermediateFilter.district === "all") {
            setClearAll({
                district: true,
                village: true,
                hamlet: true,
                powerstation: true,
            });
            setIntermediateFilter({ district: "", village: "", hamlet: "" });
        }
        if (intermediateFilter.village === "all") {
            setClearAll((prev) => ({ ...prev, village: true, hamlet: true }));
        }
        if (intermediateFilter.hamlet === "all") {
            setClearAll((prev) => ({ ...prev, hamlet: true }));
        }
        if (defaultRegions.district !== intermediateFilter.district) {
            setClearAll((prev) => ({ ...prev, village: true, hamlet: true }));
            setDefaultRegions((prev) => ({
                ...prev,
                district: intermediateFilter.district,
            }));
        }
        return () => setClearAll(initialClearAll);
    }, [
        intermediateFilter.district,
        intermediateFilter.village,
        intermediateFilter.hamlet,
        intermediateFilter.powerstation,
    ]);

    // Handle billing month changes
    useEffect(() => {
        if (billingMonth) {
            const [year, month] = billingMonth.split("-") ?? ["", ""];
            setIntermediateFilter((prev: any) => ({
                ...prev,
                year,
                month: parseInt(month),
            }));
        }
    }, [billingMonth]);

    // Submit handler
    const handleSubmit = async (ev: any) => {
        console.log("Handle Submit triggers");

        ev.preventDefault();
        setLoading(true);
        try {
            const res = (await POST("/reading/update-dates", {
                ...intermediateFilter,
                issueDate,
                readingDate,
                dueDate,
                meterId:
                    intermediateFilter?.meterNo ?? [].map((m: any) => m?.id),
                meterNo: undefined,
            })) as any;

            setFeedback({
                message: res.data.message,
                severity: "success",
                show: true,
            });
            setLoading(false);
        } catch (error: any) {
            console.log(error, "error");
            setFeedback({
                message:
                    error?.response?.data?.message ||
                    "failed to changed reading",
                severity: "error",
                show: true,
            });
            setLoading(false);
        }
    };

    return (
        <>
            <Typography variant="h5" style={{ padding: "1rem" }}>
                Update Readings Date
            </Typography>

            <Grid container spacing={2}>
                {/* District */}

                <MultiPermissionAuthorize ops={["READ DISTRICT"]}>
                    <Grid item xs={12} md={6} lg={3}>
                        <UpdatedSearchableInput
                            label="District"
                            api="/region/district"
                            filter={intermediateFilter}
                            setFilter={setIntermediateFilter}
                            clearAll={clearAll.district}
                        />
                    </Grid>
                </MultiPermissionAuthorize>

                {/* Powerstation */}
                <MultiPermissionAuthorize ops={["READ POWERSTATION"]}>
                    <Grid item xs={12} md={6} lg={3}>
                        <UpdatedSearchableInput
                            label="Powerstation"
                            api="/powerstation"
                            filter={intermediateFilter}
                            setFilter={setIntermediateFilter}
                            dep={intermediateFilter.district}
                            params={{ district: intermediateFilter.district }}
                            clearAll={clearAll.powerstation}
                        />
                    </Grid>
                </MultiPermissionAuthorize>

                {/* Village */}
                <MultiPermissionAuthorize ops={["READ VILLAGE"]}>
                    <Grid item xs={12} md={6} lg={3}>
                        <UpdatedSearchableInput
                            label="Village"
                            api="/region/village"
                            filter={intermediateFilter}
                            setFilter={setIntermediateFilter}
                            dep={[
                                intermediateFilter.district,
                                intermediateFilter.powerstation,
                            ]}
                            params={{
                                district: intermediateFilter.district,
                                powerstation: intermediateFilter.powerstation,
                            }}
                            clearAll={clearAll.village}
                        />
                    </Grid>
                </MultiPermissionAuthorize>

                {/* Hamlet */}
                <MultiPermissionAuthorize ops={["READ HAMLET"]}>
                    <Grid item xs={12} md={6} lg={3}>
                        <UpdatedSearchableInput
                            label="Hamlet"
                            api="/region/hamlet"
                            filter={intermediateFilter}
                            setFilter={setIntermediateFilter}
                            dep={[
                                intermediateFilter.village,
                                intermediateFilter.powerstation,
                            ]}
                            params={{
                                village: intermediateFilter.village,
                                powerstation: intermediateFilter.powerstation,
                            }}
                            multiple={false}
                            clearAll={clearAll.hamlet}
                        />
                    </Grid>
                </MultiPermissionAuthorize>

                <MultiPermissionAuthorize ops={["READ METER"]}>
                    <Grid item xs={12} md={6} lg={3}>
                        <UpdatedSearchableInput
                            api="/customer/meter"
                            filter={intermediateFilter}
                            setFilter={setIntermediateFilter}
                            _name="meterNo"
                            label="Meter No."
                            dep={[
                                intermediateFilter?.district,
                                intermediateFilter?.village,
                                intermediateFilter?.hamlet,
                                intermediateFilter?.powerstation,
                            ]}
                            params={{
                                district:
                                    intermediateFilter?.district &&
                                    intermediateFilter.district !== "all"
                                        ? intermediateFilter.district
                                        : undefined,
                                village:
                                    intermediateFilter?.village &&
                                    intermediateFilter.village !== "all"
                                        ? intermediateFilter.village
                                        : undefined,
                                hamlet:
                                    intermediateFilter?.hamlet &&
                                    intermediateFilter.hamlet !== "all"
                                        ? intermediateFilter.hamlet
                                        : undefined,
                                powerstation:
                                    intermediateFilter?.powerstation &&
                                    intermediateFilter.powerstation !== "all"
                                        ? intermediateFilter.powerstation
                                        : undefined,
                                status: "active",
                            }}
                            optionsPreprocessor={(opt: any) => ({
                                ...opt,
                                name: opt.meterNo,
                            })}
                            multiple
                            getFullRecord
                        />
                    </Grid>
                </MultiPermissionAuthorize>

                {/* Billing Month */}
                <Grid item xs={12} md={6} lg={3}>
                    <TextField
                        size="small"
                        name="billingMonth"
                        label="Billing month"
                        type="month"
                        fullWidth
                        value={billingMonth}
                        required
                        onChange={(e) => setBillingMonth(e.target.value)}
                        inputProps={{
                            max: currentMonth, // ✅ prevents forward months
                        }}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            endAdornment: billingMonth ? (
                                <IconButton
                                    size="small"
                                    onClick={() => setBillingMonth("")}
                                >
                                    <CloseOutlined />
                                </IconButton>
                            ) : null,
                        }}
                    />
                </Grid>

                {/* Reading Date */}
                <Grid item xs={12} md={6} lg={3}>
                    <TextField
                        name="readingDate"
                        value={readingDate}
                        label="Reading Date"
                        fullWidth
                        size="small"
                        type="date"
                        onChange={(ev) => setReadingDate(ev.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>

                {/* Issue Date */}
                <Grid item xs={12} md={6} lg={3}>
                    <TextField
                        name="issueDate"
                        value={issueDate}
                        label="Issue Date"
                        fullWidth
                        size="small"
                        type="date"
                        onChange={(ev) => setIssueDate(ev.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>

                {/* Due Date */}
                <Grid item xs={12} md={6} lg={3}>
                    <TextField
                        name="dueDate"
                        value={dueDate}
                        label="Due Date"
                        fullWidth
                        size="small"
                        type="date"
                        onChange={(ev) => setDueDate(ev.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>

                {/* Submit Button */}
                <Grid
                    item
                    xs={12}
                    md={6}
                    lg={3}
                    display="flex"
                    alignItems="center"
                >
                    <Button
                        variant="outlined"
                        color="secondary"
                        fullWidth
                        onClick={handleSubmit}
                        endIcon={
                            loading ? (
                                <CircularProgress
                                    color="secondary"
                                    size="1rem"
                                />
                            ) : (
                                ""
                            )
                        }
                        disabled={
                            !intermediateFilter.district &&
                            !intermediateFilter.village &&
                            !intermediateFilter.hamlet &&
                            !intermediateFilter.powerstation &&
                            (!intermediateFilter.meterNo ||
                                intermediateFilter.meterNo.length === 0) &&
                            !billingMonth
                        }
                    >
                        Update Readings Date
                    </Button>
                </Grid>

                {/* Feedback Message */}
                {feedback.show && (
                    <Grid item xs={12}>
                        <Alert severity={feedback.severity}>
                            {feedback.message}
                        </Alert>
                    </Grid>
                )}
            </Grid>
        </>
    );
};

export default UpdateReadingDate;
